import { select, takeEvery, put } from 'redux-saga/effects'

import { get, createType } from 'lib/middleware/api'
import PreviewTokenManager from 'utils/previewTokenManager'
import { isProgramFlexPathname } from 'layers/learn/content/utils/verify'

import { getHighestIncludeOption } from '../utils/index'

import ContenfulTypes from './types'
import ContentfulActions from './actions'

export const CONTENTFUL_PREVIEW_DOMAIN = 'preview.contentful.com'
export const CONTENTFUL_PROD_DOMAIN = 'cdn.contentful.com'

const PreviewTokenMngr = new PreviewTokenManager()

export const getEntry = (
  entryId,
  locale,
  preview,
  format,
  url,
  include,
  isPageLevel = false,
) => {
  const urlParameters = {
    entryId,
    locale,
    preview,
    format,
    url,
    include,
    isPageLevel,
  }
  return ContentfulActions.setupContentfulGet(urlParameters)
}

export const makeContentfulGetCall = ({
  entryId,
  locale,
  preview,
  format,
  url,
  include,
  contentLifeCycleTypes,
  isPageLevel = false,
}) => {
  const fullUrl = createEndpoint(
    entryId,
    locale,
    preview,
    format,
    url,
    include,
    isPageLevel,
  )
  const typesOrdered = contentLifeCycleTypes.map(type =>
    createType(type, { entryId, locale, include }),
  )

  return get(fullUrl, typesOrdered, process.env.CONTENTFUL_TOKEN)
}

export const createEndpoint = (
  entryId,
  locale,
  preview,
  format,
  url = '',
  include,
  isPageLevel = false,
) => {
  const environment = preview
    ? CONTENTFUL_PREVIEW_DOMAIN
    : CONTENTFUL_PROD_DOMAIN
  const separator = url.includes('?') ? '&' : '?'
  const endpoint = `${url}${separator}${makeQueries(
    entryId,
    locale,
    environment,
    format,
    include,
    isPageLevel,
  )}`
  return endpoint
}

export const makeQueries = (
  entryId,
  locale,
  environment,
  format,
  include,
  isPageLevel = false,
) => {
  const environmentObject = environment ? { environment } : {}
  const cache = PreviewTokenMngr.getToken()

  const parameters = {
    entryId,
    format,
    include,
    locale,
    ...environmentObject,
  }

  // Add isPageLevel flag to URL for API middleware check (fallback)
  if (isPageLevel) {
    parameters.isPageLevel = 'true'
  }

  // TODO for previewToken feature. Remove after we have a better solution
  if (cache) {
    parameters.cache = cache
  }

  return encodeQueryData(parameters)
}

export function encodeQueryData(data) {
  const ret = []

  for (let d in data) {
    ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]))
  }

  return ret.join('&')
}

// Helper function to check if current route is a program flex route (recursive - matches any depth)
function* isCurrentRouteProgramFlex() {
  // Prioritize window.location.pathname as it's always up-to-date, fallback to router state
  let pathname = null
  if (
    typeof window !== 'undefined' &&
    window.location &&
    window.location.pathname
  ) {
    pathname = window.location.pathname
  } else {
    const state = yield select()
    pathname = state?.router?.location?.pathname
  }

  return isProgramFlexPathname(pathname)
}

// saga funcs
function* prepareContentfulGetCall(action) {
  const { urlParams, contentLifeCycleTypes } = action
  const {
    entryId,
    locale,
    preview,
    format,
    url,
    include,
    isPageLevel = false,
  } = urlParams

  // Skip GetContentfulResource calls for program flex routes - they use content-np service
  // BUT allow individual entry fetches (like videos) which still need Contentful
  const isProgramFlex = yield isCurrentRouteProgramFlex()
  if (isProgramFlex) {
    // isPageLevel flag indicates this is a page-level fetch from content node system
    // If not set, it's an individual entry fetch (like videos from ContentEntryDataGetter)
    if (isPageLevel) {
      // Block page-level content fetches - they should use content-np service
      return
    }
    // Otherwise allow it (individual entries like videos)
  }

  const highestEntryWithInclude = yield select(state =>
    getHighestIncludeOption(state, entryId, locale),
  )
  const { include: highestInclude } = highestEntryWithInclude

  const finalInclude = highestInclude > include ? highestInclude : include

  yield put(
    makeContentfulGetCall({
      entryId,
      locale,
      preview,
      format,
      url,
      include: finalInclude,
      contentLifeCycleTypes,
      isPageLevel,
    }),
  )
}

export function* watchForSetupContentfulGet() {
  yield takeEvery(ContenfulTypes.SETUP_CONTENTFUL_GET, prepareContentfulGetCall)
}
